// Case meta strip + relativeTime helper.
//
// Compact one-row metadata under the H1. The phase used to live as a
// loud uppercase pill beside the title; that gave "TRIAGE" — the resting
// state for half of every user's session — the same visual weight as a
// real alarm. The phase is metadata, so it joins the rest of the
// metadata: phase · €X paid · X ago. Same muted tone as the rest of the
// line.

import type { Case } from '../cases';
import { useLocale } from '../locale';
import { m } from '../paraglide/messages';
import { useSummary } from '../summary';

function phaseLabel(status: Case['status']): string {
  switch (status) {
    case 'triage':
      return m.phaseTriage();
    case 'intake':
      return m.phaseIntake();
    case 'review':
      return m.phaseReview();
    case 'paid':
      return m.phasePaid();
    case 'closed':
      return m.phaseClosed();
  }
}

// Locale-aware "x ago" — paraglide messages handle plural/singular per
// language. Pulls from m.relativeXxxOne / m.relativeXxxMany. The threshold
// boundaries are language-agnostic; only the surface text changes.
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return m.relativeJustNow();
  const min = Math.floor(sec / 60);
  if (min < 60) return min === 1 ? m.relativeMinAgoOne() : m.relativeMinAgoMany({ n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr === 1 ? m.relativeHourAgoOne() : m.relativeHourAgoMany({ n: hr });
  const day = Math.floor(hr / 24);
  if (day < 30) return day === 1 ? m.relativeDayAgoOne() : m.relativeDayAgoMany({ n: day });
  const month = Math.floor(day / 30);
  if (month < 12)
    return month === 1 ? m.relativeMonthAgoOne() : m.relativeMonthAgoMany({ n: month });
  const year = Math.floor(month / 12);
  return year === 1 ? m.relativeYearAgoOne() : m.relativeYearAgoMany({ n: year });
}

// Dot-separated line of metadata under the H1: phase · €paid · "started x ago".
// All three at the same muted weight — the H1 alone owns visual primacy.
// "started" frames the case as a journey the user is on, not a record
// being mutated. The previous "1 hour ago" by itself was ambiguous:
// updated when? "Started" anchors the time to the case's beginning,
// which is what the homeowner actually thinks about.
export function CaseMetaStrip({
  caseId,
  status,
  startedAt,
}: {
  caseId: string;
  status: Case['status'];
  startedAt: string;
}) {
  const s = useSummary(caseId);
  const [locale] = useLocale();
  const f = s.data?.key_facts;
  const parts: string[] = [phaseLabel(status)];
  if (typeof f?.totalPaidEur === 'number') {
    const currency = new Intl.NumberFormat(locale === 'pt' ? 'pt-PT' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(f.totalPaidEur);
    parts.push(m.metaPaidSuffix({ currency }));
  }
  parts.push(m.metaStartedPrefix({ ago: relativeTime(startedAt) }));

  return (
    <p className="case-meta-line">
      {parts.map((part, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stable derived list
        <span key={i}>
          {i > 0 ? (
            <span className="meta-sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          {part}
        </span>
      ))}
    </p>
  );
}
